import { useCampaignApproval } from 'hooks/useCampaignApproval'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt, useHistory, useParams } from 'react-router-dom'

import { EuiButton, EuiCallOut, EuiFlexGroup, EuiFlexItem, EuiModal, EuiModalBody, EuiModalHeader, EuiSpacer, EuiTitle, EuiLink, EuiPanel, EuiLoadingContent } from '@elastic/eui'

import { useGetMasterCampaignQuery } from 'api/rtkQueryApi/platform/masterCampaignsApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'

import { CampaignActiveStatus, CampaignStatus, CampaignSubType, CampaignType } from '../../../../api/interfaces'
import { useApproveCampaignMutation, useGetCampaignQuery, useSubmitCampaignForApprovalMutation } from '../../../../api/rtkQueryApi/platform/campaignsApi'
import { useGetCreativesQuery } from '../../../../api/rtkQueryApi/platform/creativesApi'
import { AgencyV2ClientPageLayout } from '../../../../components/layouts/AgencyV2ClientPageLayout'
import { ActivateCampaignContent } from '../ActivateCampaignContent'
import { DisapproveCampaignModal } from '../DisapproveCampaignModal'
import { fetchCampaigns } from '../../campaigns/campaignsSlice'
import { fetchCampaign, finishedLoadingAds, setCampaign as setGlobalCampaign } from '../campaignSlice'
import { CampaignEditProvider, useCampaignEdit } from './CampaignEditContext'
import { CampaignTabs } from './CampaignTabs'

const CampaignEditSaveButton: React.FC = () => {
  const { isDirty, isSaving, saveAll } = useCampaignEdit()

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
      }
    },
    [isDirty]
  )

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [handleBeforeUnload])

  return (
    <>
      <Prompt when={isDirty} message='You have unsaved changes. Are you sure you want to leave?' />
      <EuiSpacer />
      <EuiButton onClick={saveAll} isLoading={isSaving} disabled={!isDirty} fill>
        Save Campaign
      </EuiButton>
    </>
  )
}

const CampaignActivateButton: React.FC<{ onClick: () => void; disabled: boolean }> = ({ onClick, disabled }) => {
  const { isDirty, saveAll } = useCampaignEdit()

  const handleActivate = async () => {
    if (isDirty) {
      const shouldSave = window.confirm('You have unsaved changes. Save before activating?')
      if (shouldSave) {
        await saveAll()
      } else {
        return
      }
    }
    onClick()
  }

  return (
    <EuiButton onClick={handleActivate} fill disabled={disabled}>
      Activate
    </EuiButton>
  )
}

export const AgencyV2EditCampaignPage = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { campaign, isLoadingCampaign } = useSelector((state: RootState) => state.campaign)
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const [showActivateCampaignModal, setShowActivateCampaignModal] = React.useState(false)
  const [showDisapproveModal, setShowDisapproveModal] = React.useState(false)
  const { isEnabled: campaignApprovalEnabled, canApprove, canCreate } = useCampaignApproval()
  const [approveCampaign, approveCampaignResult] = useApproveCampaignMutation()
  const [submitCampaignForApproval, submitCampaignForApprovalResult] = useSubmitCampaignForApprovalMutation()
  const { data: masterCampaign, isFetching: isFetchingMasterCampaign } = useGetMasterCampaignQuery(
    {
      masterCampaignId: campaign?.masterCampaignId ?? ''
    },
    {
      skip: !campaign?.masterCampaignId
    }
  )

  let { campaignId } = useParams<{ campaignId: string }>()
  const campaignQuery = useGetCampaignQuery(
    { campaignId, accountId: currentAccount?.id ?? '' },
    {
      skip: !currentAccount || !!campaign
    }
  )

  const ads = useGetCreativesQuery(
    {
      campaignId,
      accountId: currentAccount?.id ?? '',
      isAgency: true
    },
    {
      skip: !currentAccount
    }
  )

  const isAutoActivate = masterCampaign?.items.find(item => item.campaignId === campaignId)?.autoActivate

  useEffect(() => {
    if (!!campaignQuery.data && !campaign) {
      dispatch(setGlobalCampaign(campaignQuery.data))
    }
  }, [campaign, campaignQuery.data, dispatch])

  useEffect(() => {
    if (!!ads.data) {
      dispatch(finishedLoadingAds(ads.data))
    }
  }, [ads.data, dispatch])

  const getCampaignType = () => {
    switch (campaign?.type) {
      case CampaignType.Billboard:
        return 'Digital Billboard Campaign'
      case CampaignType.Internet:
        if (campaign?.campaignSubType === CampaignSubType.Display) {
          return 'Internet Display Campaign'
        } else if (campaign?.campaignSubType === CampaignSubType.Native) {
          return 'Internet Native Campaign'
        } else {
          return 'Internet Campaign'
        }
      case CampaignType.TV:
        return 'Connected TV Campaign'
      default:
        return 'Campaign'
    }
  }

  const onActivateClick = () => {
    setShowActivateCampaignModal(true)
  }

  const isPendingApproval = campaignApprovalEnabled && campaign?.status === CampaignStatus.PendingApproval
  const isApprovedCampaign = campaignApprovalEnabled && campaign?.status === CampaignStatus.SavedForLater
  const isDisapprovedCampaign = campaignApprovalEnabled && campaign?.status === CampaignStatus.Disapproved
  const hideActivateForApprovedNonApprover = campaignApprovalEnabled && !canApprove && isApprovedCampaign

  const refreshCampaign = () => {
    if (!currentAccount || !campaign) return
    dispatch(fetchCampaign(currentAccount.id, campaign.id))
    dispatch(fetchCampaigns(currentAccount.id))
  }

  const onApprove = async () => {
    if (!currentAccount || !campaign) return
    try {
      const result = await approveCampaign({ accountId: currentAccount.id, campaignId: campaign.id }).unwrap()
      if (result.requiresPaymentActivation) {
        setShowActivateCampaignModal(true)
      } else {
        dispatch(showSuccessToast(result.activated ? 'Campaign approved and activated' : 'Campaign approved'))
      }
      refreshCampaign()
    } catch {
      dispatch(showErrorToast('Failed to approve campaign'))
    }
  }

  const onSubmitForApproval = async () => {
    if (!currentAccount || !campaign) return
    try {
      await submitCampaignForApproval({ accountId: currentAccount.id, campaignId: campaign.id }).unwrap()
      dispatch(showSuccessToast('Campaign submitted for approval'))
      refreshCampaign()
    } catch {
      dispatch(showErrorToast('Failed to submit campaign for approval'))
    }
  }

  const headerTitleComponent = () => {
    return (
      <EuiFlexGroup>
        <EuiFlexItem grow={false}>
          <EuiTitle>
            <h1>
              {currentAccount?.name}
              <br />
            </h1>
          </EuiTitle>
          <EuiLink
            color='success'
            onClick={() => {
              history.push(`/masterCampaigns/${campaign?.masterCampaignId}`)
            }}
          >
            <EuiTitle size='s'>
              <h2 style={{ color: '#007e77' }}>Master Campaign: {masterCampaign?.name}</h2>
            </EuiTitle>
          </EuiLink>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  const headerTitle = masterCampaign ? headerTitleComponent() : `Solo Campaign: ${currentAccount?.name}`

  if ((isLoadingCampaign && !campaign) || isFetchingMasterCampaign) {
    return (
      <EuiPanel grow={false}>
        <EuiLoadingContent />
      </EuiPanel>
    )
  }

  return (
    <AgencyV2ClientPageLayout helmetTitle={'headerTitle'} headerTitle={headerTitle} isLoading={isFetchingMasterCampaign || !campaign} panelTitle={campaign?.masterCampaignId ? `Campaign: ${campaign?.name}` : getCampaignType() + ': ' + campaign?.name} panelDescription={campaign?.masterCampaignId ? getCampaignType() : ''} boldPanelDescription>
      <CampaignEditProvider>
        {campaign?.activeStatus === CampaignActiveStatus.Inactive && !isAutoActivate && (
          <>
            {isPendingApproval && !canApprove && <EuiCallOut title='Awaiting approval' iconType='clock' color='warning' />}
            {isPendingApproval && canApprove && (
              <EuiFlexGroup>
                <EuiFlexItem grow={false}>
                  <EuiButton fill onClick={onApprove} isLoading={approveCampaignResult.isLoading}>
                    Approve
                  </EuiButton>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton color='danger' onClick={() => setShowDisapproveModal(true)}>
                    Disapprove
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            )}
            {isDisapprovedCampaign && (
              <EuiCallOut title='Campaign disapproved' iconType='alert' color='danger'>
                {campaign?.disapprovedReason && <p>{campaign.disapprovedReason}</p>}
                {canCreate && (
                  <EuiButton onClick={onSubmitForApproval} isLoading={submitCampaignForApprovalResult.isLoading}>
                    Submit for approval
                  </EuiButton>
                )}
              </EuiCallOut>
            )}
            {!isPendingApproval && !isDisapprovedCampaign && !hideActivateForApprovedNonApprover && <CampaignActivateButton onClick={onActivateClick} disabled={!campaign?.hasEditedBudget && campaign?.status === CampaignStatus.SavedForLater} />}
            <EuiSpacer />
          </>
        )}
        {showDisapproveModal && currentAccount && campaign && <DisapproveCampaignModal accountId={currentAccount.id} campaignId={campaign.id} onClose={() => setShowDisapproveModal(false)} onDisapproved={refreshCampaign} />}
        <CampaignTabs />
        <CampaignEditSaveButton />
      </CampaignEditProvider>
      {showActivateCampaignModal && campaign && !isAutoActivate && (
        <EuiModal onClose={() => setShowActivateCampaignModal(false)}>
          <EuiModalHeader>
            <EuiTitle>
              <h2>Activate Campaign</h2>
            </EuiTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <ActivateCampaignContent campaignId={campaign.id} amount={campaign.budget} campaignType={campaign.type} onActivated={() => setShowActivateCampaignModal(false)} />
          </EuiModalBody>
        </EuiModal>
      )}
    </AgencyV2ClientPageLayout>
  )
}
